import { useState } from 'react';

interface AlertsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function AlertsModal({ isOpen, onClose }: AlertsModalProps) {
  const [activeTab, setActiveTab] = useState<'price' | 'arrival'>('price');
  const [priceAlerts, setPriceAlerts] = useState<Array<{
    id: number;
    make: string;
    model: string;
    maxPrice: number;
    email: string;
    active: boolean;
  }>>([
    { id: 1, make: 'Toyota', model: 'RAV4', maxPrice: 28000, email: 'user@example.com', active: true },
    { id: 2, make: 'Honda', model: 'Accord', maxPrice: 25000, email: 'user@example.com', active: true }
  ]);

  const [arrivalAlerts, setArrivalAlerts] = useState<Array<{
    id: number;
    make: string;
    model: string;
    yearFrom: number;
    email: string;
    active: boolean;
  }>>([
    { id: 1, make: 'Ford', model: 'F-150', yearFrom: 2020, email: 'user@example.com', active: true }
  ]);

  const [newPriceAlert, setNewPriceAlert] = useState({
    make: '',
    model: '',
    maxPrice: '',
    email: ''
  });

  const [newArrivalAlert, setNewArrivalAlert] = useState({
    make: '',
    model: '',
    yearFrom: '',
    email: ''
  });

  const handleAddPriceAlert = () => {
    if (newPriceAlert.make && newPriceAlert.model && newPriceAlert.maxPrice && newPriceAlert.email) {
      setPriceAlerts([...priceAlerts, {
        id: Date.now(),
        make: newPriceAlert.make,
        model: newPriceAlert.model,
        maxPrice: parseInt(newPriceAlert.maxPrice),
        email: newPriceAlert.email,
        active: true
      }]);
      setNewPriceAlert({ make: '', model: '', maxPrice: '', email: '' });
    }
  };

  const handleAddArrivalAlert = () => {
    if (newArrivalAlert.make && newArrivalAlert.model && newArrivalAlert.yearFrom && newArrivalAlert.email) {
      setArrivalAlerts([...arrivalAlerts, {
        id: Date.now(),
        make: newArrivalAlert.make,
        model: newArrivalAlert.model,
        yearFrom: parseInt(newArrivalAlert.yearFrom),
        email: newArrivalAlert.email,
        active: true
      }]);
      setNewArrivalAlert({ make: '', model: '', yearFrom: '', email: '' });
    }
  };

  const togglePriceAlert = (id: number) => {
    setPriceAlerts(priceAlerts.map(alert => 
      alert.id === id ? { ...alert, active: !alert.active } : alert
    ));
  };

  const toggleArrivalAlert = (id: number) => {
    setArrivalAlerts(arrivalAlerts.map(alert => 
      alert.id === id ? { ...alert, active: !alert.active } : alert
    ));
  };

  const deletePriceAlert = (id: number) => {
    setPriceAlerts(priceAlerts.filter(alert => alert.id !== id));
  };

  const deleteArrivalAlert = (id: number) => {
    setArrivalAlerts(arrivalAlerts.filter(alert => alert.id !== id));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-gray-900">Alert Preferences</h2>
            <button
              onClick={onClose}
              className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-2xl text-gray-600"></i>
            </button>
          </div>

          {/* Tabs */}
          <div className="flex gap-2">
            <button
              onClick={() => setActiveTab('price')}
              className={`px-6 py-2 rounded-full font-medium transition-all cursor-pointer whitespace-nowrap ${
                activeTab === 'price'
                  ? 'bg-[#2563EB] text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <i className="ri-price-tag-3-line mr-2"></i>
              Price Alerts
            </button>
            <button
              onClick={() => setActiveTab('arrival')}
              className={`px-6 py-2 rounded-full font-medium transition-all cursor-pointer whitespace-nowrap ${
                activeTab === 'arrival'
                  ? 'bg-[#2563EB] text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <i className="ri-notification-line mr-2"></i>
              New Arrivals
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'price' ? (
            <div className="space-y-6">
              {/* Add New Price Alert */}
              <div className="bg-[#F8F9FA] rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-4">Create Price Alert</h3>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <input
                    type="text"
                    placeholder="Make (e.g., Toyota)"
                    value={newPriceAlert.make}
                    onChange={(e) => setNewPriceAlert({ ...newPriceAlert, make: e.target.value })}
                    className="px-4 py-3 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]"
                  />
                  <input
                    type="text"
                    placeholder="Model (e.g., Camry)"
                    value={newPriceAlert.model}
                    onChange={(e) => setNewPriceAlert({ ...newPriceAlert, model: e.target.value })}
                    className="px-4 py-3 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]"
                  />
                  <input
                    type="number"
                    placeholder="Max Price"
                    value={newPriceAlert.maxPrice}
                    onChange={(e) => setNewPriceAlert({ ...newPriceAlert, maxPrice: e.target.value })}
                    className="px-4 py-3 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]"
                  />
                  <input
                    type="email"
                    placeholder="Email Address"
                    value={newPriceAlert.email}
                    onChange={(e) => setNewPriceAlert({ ...newPriceAlert, email: e.target.value })}
                    className="px-4 py-3 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]"
                  />
                </div>
                <button
                  onClick={handleAddPriceAlert}
                  className="w-full py-3 bg-[#2563EB] text-white rounded-lg font-medium hover:bg-[#1d4ed8] transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-add-line mr-2"></i>
                  Add Price Alert
                </button>
              </div>

              {/* Active Price Alerts */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-4">Active Price Alerts ({priceAlerts.length})</h3>
                <div className="space-y-3">
                  {priceAlerts.map(alert => (
                    <div
                      key={alert.id}
                      className="bg-white border-2 border-gray-200 rounded-xl p-4 flex items-center justify-between"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h4 className="font-semibold text-gray-900">
                            {alert.make} {alert.model}
                          </h4>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            alert.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                          }`}>
                            {alert.active ? 'Active' : 'Paused'}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600">
                          Alert when price drops below <strong>${alert.maxPrice.toLocaleString()}</strong>
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          <i className="ri-mail-line mr-1"></i>
                          {alert.email}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => togglePriceAlert(alert.id)}
                          className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
                          title={alert.active ? 'Pause' : 'Activate'}
                        >
                          <i className={`ri-${alert.active ? 'pause' : 'play'}-line text-xl text-gray-600`}></i>
                        </button>
                        <button
                          onClick={() => deletePriceAlert(alert.id)}
                          className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-red-50 transition-colors cursor-pointer"
                          title="Delete"
                        >
                          <i className="ri-delete-bin-line text-xl text-red-600"></i>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Add New Arrival Alert */}
              <div className="bg-[#F8F9FA] rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-4">Create New Arrival Alert</h3>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <input
                    type="text"
                    placeholder="Make (e.g., Ford)"
                    value={newArrivalAlert.make}
                    onChange={(e) => setNewArrivalAlert({ ...newArrivalAlert, make: e.target.value })}
                    className="px-4 py-3 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]"
                  />
                  <input
                    type="text"
                    placeholder="Model (e.g., F-150)"
                    value={newArrivalAlert.model}
                    onChange={(e) => setNewArrivalAlert({ ...newArrivalAlert, model: e.target.value })}
                    className="px-4 py-3 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]"
                  />
                  <input
                    type="number"
                    placeholder="Year From (e.g., 2020)"
                    value={newArrivalAlert.yearFrom}
                    onChange={(e) => setNewArrivalAlert({ ...newArrivalAlert, yearFrom: e.target.value })}
                    className="px-4 py-3 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]"
                  />
                  <input
                    type="email"
                    placeholder="Email Address"
                    value={newArrivalAlert.email}
                    onChange={(e) => setNewArrivalAlert({ ...newArrivalAlert, email: e.target.value })}
                    className="px-4 py-3 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]"
                  />
                </div>
                <button
                  onClick={handleAddArrivalAlert}
                  className="w-full py-3 bg-[#2563EB] text-white rounded-lg font-medium hover:bg-[#1d4ed8] transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-add-line mr-2"></i>
                  Add Arrival Alert
                </button>
              </div>

              {/* Active Arrival Alerts */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-4">Active Arrival Alerts ({arrivalAlerts.length})</h3>
                <div className="space-y-3">
                  {arrivalAlerts.map(alert => (
                    <div
                      key={alert.id}
                      className="bg-white border-2 border-gray-200 rounded-xl p-4 flex items-center justify-between"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h4 className="font-semibold text-gray-900">
                            {alert.make} {alert.model}
                          </h4>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            alert.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                          }`}>
                            {alert.active ? 'Active' : 'Paused'}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600">
                          Notify when {alert.yearFrom} or newer arrives
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          <i className="ri-mail-line mr-1"></i>
                          {alert.email}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => toggleArrivalAlert(alert.id)}
                          className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
                          title={alert.active ? 'Pause' : 'Activate'}
                        >
                          <i className={`ri-${alert.active ? 'pause' : 'play'}-line text-xl text-gray-600`}></i>
                        </button>
                        <button
                          onClick={() => deleteArrivalAlert(alert.id)}
                          className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-red-50 transition-colors cursor-pointer"
                          title="Delete"
                        >
                          <i className="ri-delete-bin-line text-xl text-red-600"></i>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 bg-[#F8F9FA]">
          <div className="flex items-start gap-3">
            <i className="ri-information-line text-xl text-[#2563EB] mt-0.5"></i>
            <div className="flex-1">
              <p className="text-sm text-gray-600">
                You'll receive email notifications when vehicles matching your criteria become available or when prices drop below your target.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
